import React, { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import i18n from "@/i18n";
import { reloadOnceForChunkError } from "./chunkReload";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (reloadOnceForChunkError(error)) return;
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    } else {
      console.error("[ErrorBoundary] Caught error:", error, errorInfo);
    }
  }

  resetError = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback({
            error: this.state.error,
            resetError: this.resetError,
          });
        }
        return this.props.fallback;
      }

      const t = (key, fallback) => (i18n ? i18n.t(key, fallback) : fallback);

      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center p-4 text-center my-4"
          style={{
            backgroundColor: "var(--bg-elevated, #ffffff)",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: "12px",
            minHeight: "260px",
          }}
          data-testid="error-boundary-fallback"
        >
          <div
            className="d-flex align-items-center justify-content-center mb-3 rounded-circle"
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              color: "#ef4444",
            }}
          >
            <AlertTriangle size={24} />
          </div>
          <h5 className="fw-bold mb-2 text-dark">
            {t("errorBoundary.title", "Something went wrong")}
          </h5>
          <p className="text-muted mb-4 small" style={{ maxWidth: "440px" }}>
            {t(
              "errorBoundary.message",
              "An unexpected error occurred while rendering this section.",
            )}
          </p>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 btn-sm rounded-pill px-3"
              onClick={this.resetError}
              data-testid="error-boundary-retry-btn"
            >
              <RotateCcw size={14} />
              {t("errorBoundary.retry", "Try Again")}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary d-flex align-items-center gap-2 btn-sm rounded-pill px-3"
              onClick={() => window.location.assign("/geo-dashboard")}
              data-testid="error-boundary-dashboard-btn"
            >
              {t("errorBoundary.goToDashboard", "Go to Dashboard")}
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2 btn-sm rounded-pill px-3"
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload-btn"
            >
              {t("errorBoundary.reload", "Reload Page")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
